import React, { useCallback, useContext, useState } from "react";
import cn from "classnames";
import { CellContext } from "@tanstack/react-table";
import { Trans } from "react-i18next";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { PencilSquareIcon } from "@heroicons/react/20/solid";
import useShowMessage from "oneclick-component/src/hooks/useShowMessage";
import { ActionDialog } from "oneclick-component/src/components/Modal";
import { Button } from "oneclick-component/src/components/Button";
import { t } from "i18next";
import { DotsHorizon } from "oneclick-component/src/icon";
import { useMakeExpireShiftHandlerShiftsShiftIdMakeExpirePostMutation as useMakeExpire } from "oneclick-component/src/store/apis/enhancedApi";
import { useShowError } from "../../../hooks/useShowError";
import ProgressBar from "oneclick-component/src/components/ProgressBar";
import { StopIcon } from "../../../icon";
import HireStatusPopup from "../HireStatusPopup";
import PopMenu, {
  PopMenuItem,
} from "oneclick-component/src/components/PopMenu";
import AppRoutes from "../../../routes/AppRoutes";
import CancelShiftDialog from "../../../components/CancelShiftDialog/CancelShiftDialog";
import { ShiftListContext } from "../ShiftListContext";
import ShiftFullTimeAttendanceDialog from "../../../components/ShiftFullTimeAttendanceDialog";
import { ActiveShiftListRecord } from "./useActiveShiftListTable";

interface FullTimeStatusSectionProps {
  isFullTimeManageOpen: boolean;
  onFullTimeManageOpen: () => void;
  onFullTimeManageClose: () => void;
  record: ActiveShiftListRecord;
}

const FullTimeStatusSection = (props: FullTimeStatusSectionProps) => {
  const {
    record,
    isFullTimeManageOpen,
    onFullTimeManageOpen,
    onFullTimeManageClose,
  } = props;

  return (
    <div>
      <ProgressBar
        max={record.fullTimeCount}
        count={record.hiredFullTimeCount}
        labelStyle="top"
        barSize="l"
        labelClassName="text-red-600"
        className={cn("mt-8", "mb-2")}
        title={t("shiftList.table.incident.hireStatus.fullTime")}
      />
      <button
        onClick={onFullTimeManageOpen}
        type="button"
        className={cn(
          "text-red-600",
          "text-xs",
          "font-medium",
          "flex",
          "flex-row",
          "items-center"
        )}
      >
        <PencilSquareIcon className={cn("h-3", "w-3", "mr-0.5")} />
        <u>
          <Trans i18nKey="fulltimeAttendance.action.updateList" />
        </u>
      </button>
      <ShiftFullTimeAttendanceDialog
        isOpen={isFullTimeManageOpen}
        onClose={onFullTimeManageClose}
        shiftId={record.id}
        fulltimeCount={record.fullTimeCount}
      />
    </div>
  );
};

export const ActiveShiftListTableAction = ({
  row,
}: CellContext<ActiveShiftListRecord, unknown>): React.ReactElement | null => {
  const showMessage = useShowMessage();
  const { showError } = useShowError();
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [isCloseIncidentDialogOpen, setIsCloseIncidentDialogOpen] =
    useState(false);
  const onCloseCloseIncidentDialog = useCallback(
    () => setIsCloseIncidentDialogOpen(false),
    []
  );
  const { shiftCancelReasons } = useContext(ShiftListContext);

  const [makeExpire, { isLoading: isMakeExpireLoading }] = useMakeExpire();

  const isIncident = row.original.shiftType === "INCIDENT";
  const { canEdit: canEditIncident, canCancel: canCancelIncident } =
    row.original.incidentAccessControl ?? {};

  const shouldRenderIncidentActions =
    isIncident && (canEditIncident || canCancelIncident);
  const shouldRender = !isIncident || shouldRenderIncidentActions;

  const onConfirmCloseIncident = useCallback(async () => {
    makeExpire({ shiftId: row.original.id })
      .unwrap()
      .then(() => {
        showMessage({
          type: "success",
          title: t("shiftDetail.closeIncident.dialog.success.title"),
          message: t("shiftDetail.closeIncident.dialog.success.description"),
        });
      })
      .catch((err) => {
        showError(err, "shiftDetail.closeIncident.dialog.fail.title");
      });
  }, [makeExpire, row, showError, showMessage]);

  const onClickCloseIncidentShift = useCallback(() => {
    setIsCloseIncidentDialogOpen(true);
  }, []);
  const onCloseCancelDialog = useCallback(() => {
    setIsCancelDialogOpen(false);
  }, []);
  const onOpenCancelDialog = useCallback(
    (e?: React.MouseEvent<HTMLButtonElement>) => {
      e?.stopPropagation();
      setIsCancelDialogOpen(true);
    },
    []
  );
  const popMenuControlElementClass = useCallback(
    ({ open }: { open: boolean }) =>
      cn(
        "flex",
        "max-w-xs",
        "p-2", // for larger clickable area
        "mx-auto",
        "items-center",
        "rounded-full",
        "text-sm",
        "focus:outline-none",
        "focus:ring-2",
        "focus:ring-primary-500",
        "focus:ring-offset-2",
        {
          "fill-gray-500": !open && !isIncident,
          "fill-red-600": !open && isIncident,
          "fill-primary-500": open,
          "bg-white": !isIncident,
          "bg-red-50": isIncident,
        }
      ),
    [isIncident]
  );

  const stopPropagate = useCallback((e?: React.MouseEvent<HTMLElement>) => {
    e?.stopPropagation();
  }, []);

  if (!shouldRender) {
    return null;
  }
  return (
    <div onClick={stopPropagate}>
      {isIncident ? (
        <div>
          <Button
            prefixIcon={StopIcon}
            className={cn("bg-red-600", "hover:bg-red-800")}
            isFlashy={true}
            flashyClassName={cn("!bg-red-600")}
            onClick={onClickCloseIncidentShift}
            title={t("shiftList.table.incident.tooltip")}
          >
            <Trans i18nKey="shiftList.table.incident.closeShift" />
          </Button>
          <ActionDialog
            title={t("shiftDetail.closeIncidentShift.dialog.title")}
            actionButtonText={t(
              "shiftDetail.closeIncidentShift.dialog.action.confirm"
            )}
            cancelButtonText={t("common.cancel")}
            isOpen={isCloseIncidentDialogOpen}
            onClose={onCloseCloseIncidentDialog}
            onClickActionButton={onConfirmCloseIncident}
            isActionLoading={isMakeExpireLoading}
            titleIcon={
              <ExclamationTriangleIcon
                className={cn("w-6", "h-6", "mr-2", "text-black")}
              />
            }
          />
        </div>
      ) : (
        <div>
          <PopMenu
            // workaround cell padding asymmertic
            menuClass={cn("relative", "pr-1", "sm:pr-3")}
            controlElementClass={popMenuControlElementClass}
            controlElement={<DotsHorizon className={cn("w-5", "h-5")} />}
          >
            <div className="py-1">
              <PopMenuItem
                type="link"
                className={"text-gray-700"}
                link={AppRoutes.EditShiftScreen.render(row.original.id)}
                text={t("shiftList.tab.active.actionMenu.editShift")}
              />
              <PopMenuItem
                type="button"
                onClick={onOpenCancelDialog}
                className={"text-rose-500"}
                text={t("shiftList.tab.active.actionMenu.cancelShift")}
              />
            </div>
          </PopMenu>
          <CancelShiftDialog
            shiftIds={[row.original.id]}
            isOpen={isCancelDialogOpen}
            onClose={onCloseCancelDialog}
            cancelReasons={shiftCancelReasons}
          />
        </div>
      )}
    </div>
  );
};

export const ActiveShiftListTableHireStatus = ({
  row,
}: CellContext<ActiveShiftListRecord, unknown>): React.ReactElement | null => {
  const { original } = row;
  const isIncident = original.shiftType === "INCIDENT";
  const [isFullTimeManageOpen, setIsFullTimeManageOpen] = useState(false);

  const onFullTimeManageOpen = useCallback(() => {
    setIsFullTimeManageOpen(true);
  }, []);

  const onFullTimeManageClose = useCallback(() => {
    setIsFullTimeManageOpen(false);
  }, []);
  return (
    <HireStatusPopup
      shiftId={original.id}
      workingStationShortCode={original.hireStatus.workingStationShortCode}
      isIncident={isIncident}
      shiftRequests={original.hireStatus.hiredRequests}
      fulfillmentCount={original.hireStatus.fulfillmentCount}
      fulltimeCount={original.fullTimeCount}
      onFullTimeManageOpen={onFullTimeManageOpen}
    >
      <ProgressBar
        max={original.hireStatus.fulfillmentCount}
        count={original.hireStatus.hiredRequests.length}
        labelStyle="top"
        barSize="l"
        labelClassName={cn({ "text-red-600": isIncident })}
        title={
          isIncident ? t("shiftList.table.incident.hireStatus.partTime") : ""
        }
      />
      {isIncident ? (
        <FullTimeStatusSection
          isFullTimeManageOpen={isFullTimeManageOpen}
          onFullTimeManageOpen={onFullTimeManageOpen}
          onFullTimeManageClose={onFullTimeManageClose}
          record={original}
        />
      ) : null}
    </HireStatusPopup>
  );
};
